import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  User, Calendar, Phone, Users, Lock, CheckCircle, 
  AlertCircle, Loader2, Shield, Activity 
} from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config/api';

const API = `${BACKEND_URL}/api`;

export default function AcceptInvitation() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [invitation, setInvitation] = useState(null);
  const [teams, setTeams] = useState([]);
  
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
    date_of_birth: '',
    team_name: '',
    mobile_phone: '',
    gender: ''
  });

  useEffect(() => {
    verifyInvitation();
    fetchTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const verifyInvitation = async () => {
    try {
      const response = await axios.get(`${API}/invitations/verify/${token}`);
      setInvitation(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired invitation');
    } finally {
      setLoading(false);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/player-team/available-teams/list`);
      // Filter out "No current team" and test teams
      const filteredTeams = (response.data.teams || []).filter(t => 
        t.name && 
        t.name !== 'No current team' && 
        !t.name.toLowerCase().includes('test')
      );
      setTeams(filteredTeams);
    } catch (err) {
      console.error('Failed to fetch teams');
    }
  };

  const validateForm = () => {
    if (!formData.date_of_birth) {
      toast.error('Date of Birth is required');
      return false;
    }
    if (!formData.team_name) {
      toast.error('Team Name is required');
      return false;
    }
    if (!formData.mobile_phone || formData.mobile_phone.length < 10) {
      toast.error('Valid Mobile Phone Number is required');
      return false;
    }
    if (!formData.gender) {
      toast.error('Please select gender');
      return false;
    }
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setSubmitting(true);
    try {
      // Accept the invitation and create profile
      const response = await axios.post(`${API}/invitations/accept`, {
        invite_token: token,
        password: formData.password,
        date_of_birth: formData.date_of_birth,
        gender: formData.gender,
        team_name: formData.team_name,
        mobile_phone: formData.mobile_phone
      });
      
      toast.success('Registration successful! Welcome to BE ELITE APP');
      
      // Auto-login after registration
      try {
        const loginResponse = await axios.post(`${API}/auth/login`, {
          email: invitation.email,
          password: formData.password
        });
        
        if (loginResponse.data.access_token) {
          localStorage.setItem('token', loginResponse.data.access_token);
          navigate('/');
        } else {
          navigate('/login');
        }
      } catch {
        navigate('/login');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to complete registration');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-[#02b5ff] animate-spin mx-auto mb-4" />
          <p className="text-zinc-400">Verifying invitation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4">
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 max-w-md w-full text-center">
          <AlertCircle className="h-16 w-16 text-[#ff025f] mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Invalid Invitation</h1>
          <p className="text-zinc-400 mb-6">{error}</p>
          <Button onClick={() => navigate('/login')} className="bg-[#02b5ff]">
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-br from-[#02b5ff]/20 to-[#ff025f]/20 rounded-full flex items-center justify-center mx-auto mb-4 ring-4 ring-[#02b5ff]/20">
            <Activity className="h-10 w-10 text-[#02b5ff]" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Complete Your Registration</h1>
          <p className="text-zinc-400">
            Welcome <span className="text-[#02b5ff] font-semibold">{invitation?.first_name} {invitation?.last_name}</span>!
          </p>
          {invitation?.coach_name && (
            <p className="text-sm text-zinc-500 mt-1">
              Invited by {invitation.coach_name}
            </p>
          )}
        </div>

        {/* Form */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-6 p-3 bg-[#02b5ff]/10 rounded-lg border border-[#02b5ff]/20">
            <Shield className="h-5 w-5 text-[#02b5ff]" />
            <p className="text-sm text-[#02b5ff]">
              All fields are required to calculate accurate performance grades
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Date of Birth - REQUIRED */}
            <div>
              <Label className="text-zinc-300 flex items-center gap-2 mb-2">
                <Calendar className="h-4 w-4 text-[#02b5ff]" />
                Date of Birth <span className="text-[#ff025f]">*</span>
              </Label>
              <Input
                type="date"
                value={formData.date_of_birth}
                onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-white h-12"
                required
                max={new Date().toISOString().split('T')[0]}
              />
              <p className="text-xs text-zinc-500 mt-1">
                Required for age-based performance grading
              </p>
            </div>

            {/* Gender - REQUIRED */}
            <div>
              <Label className="text-zinc-300 flex items-center gap-2 mb-2">
                <User className="h-4 w-4 text-[#02b5ff]" />
                Gender <span className="text-[#ff025f]">*</span>
              </Label>
              <Select
                value={formData.gender}
                onValueChange={(value) => setFormData({ ...formData, gender: value })}
                required
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white h-12">
                  <SelectValue placeholder="Select gender" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="male" className="text-white">Male</SelectItem>
                  <SelectItem value="female" className="text-white">Female</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Team Name - REQUIRED */}
            <div>
              <Label className="text-zinc-300 flex items-center gap-2 mb-2">
                <Users className="h-4 w-4 text-[#02b5ff]" />
                Team Name <span className="text-[#ff025f]">*</span>
              </Label>
              <Select
                value={formData.team_name}
                onValueChange={(value) => setFormData({ ...formData, team_name: value })}
                required
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white h-12">
                  <SelectValue placeholder="Select your team" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700 max-h-60">
                  {teams.map((team) => (
                    <SelectItem key={team.id || team.name} value={team.name} className="text-white">
                      {team.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-zinc-500 mt-1">
                Select the team you're registered with
              </p>
            </div>

            {/* Mobile Phone - REQUIRED */}
            <div>
              <Label className="text-zinc-300 flex items-center gap-2 mb-2">
                <Phone className="h-4 w-4 text-[#02b5ff]" />
                Mobile Phone Number <span className="text-[#ff025f]">*</span>
              </Label>
              <Input
                type="tel"
                value={formData.mobile_phone}
                onChange={(e) => setFormData({ ...formData, mobile_phone: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-white h-12"
                placeholder="+44 7700 900000"
                required
              />
              <p className="text-xs text-zinc-500 mt-1">
                For important updates and notifications
              </p>
            </div>

            {/* Divider */}
            <div className="border-t border-zinc-800 pt-5">
              <p className="text-sm text-zinc-400 mb-4">Create your account password</p>
            </div>

            {/* Password */}
            <div>
              <Label className="text-zinc-300 flex items-center gap-2 mb-2">
                <Lock className="h-4 w-4 text-[#02b5ff]" />
                Password <span className="text-[#ff025f]">*</span>
              </Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-white h-12"
                placeholder="Min. 6 characters"
                required
                minLength={6}
              />
            </div>

            {/* Confirm Password */}
            <div>
              <Label className="text-zinc-300 flex items-center gap-2 mb-2">
                <Lock className="h-4 w-4 text-[#02b5ff]" />
                Confirm Password <span className="text-[#ff025f]">*</span>
              </Label>
              <Input
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-white h-12"
                placeholder="Confirm your password"
                required
              />
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-12 bg-gradient-to-r from-[#02b5ff] to-[#ff025f] hover:from-[#ff025f] hover:to-[#02b5ff] text-white font-semibold rounded-xl transition-all"
            >
              {submitting ? (
                <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Creating Profile...</>
              ) : (
                <><CheckCircle className="h-5 w-5 mr-2" /> Complete Registration</>
              )}
            </Button>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-zinc-500 mt-6">
          By registering, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
}
